import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlCheckDocumentFile,
    urlParamReplicaId,
    urlRegisterDocumentFile,
} from '../../../Constants';
import { IAction, SliceProps, SlicePropsWithData } from '../../store';
import { IWebDavDecisionDialogConfig } from '../../../components/General/Dialogs/WebDAVDecisionDialog/WebDAVDecisionDialog';
import { IFileSelectionDialogConfig } from '../../../components/General/Dialogs/FileSelectionDialog/FileSelectionDialog';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectFileOpenState = {
    loading: false,
    checkLoading: false,
    error: null,
    data: null,
    checkResults: [],
    webDAVDecisionDialog: null,
    fileSelectDialog: null,
    isViewFileErrorDialogOpen: false,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    showFileSelectDialog(
        state: IProjectFileOpenState,
        action: IAction<IFileSelectionDialogConfig | null>,
    ) {
        state.fileSelectDialog = action.payload;
    },
    showViewFileErrorDialog(state: IProjectFileOpenState, action: IAction<boolean>) {
        state.isViewFileErrorDialogOpen = action.payload;
    },
    showWebDAVDecisionDialog(
        state: IProjectFileOpenState,
        action: IAction<IWebDavDecisionDialogConfig | null>,
    ) {
        state.webDAVDecisionDialog = action.payload;
    },
    resetCheckResult(state: IProjectFileOpenState, action: IAction<{ callbackURL: string }>) {
        state.checkResults = state.checkResults.filter(checkResult => checkResult.callbackURL !== action.payload.callbackURL);
    },
    resetFileOpenError(state: IProjectFileOpenState) {
        state.error = null;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that registers the document for the pdfviewer.
     * @param config - Object that contains the document information to know which file needs should be opened.
     * @returns Promise for the HTTP request.
     */
    openDocumentFile: async (config: {
        replicaId: string;
        universalId: string;
        fieldName: string;
        fieldIndex: string;
        fileName?: string;
        force?: boolean;
    }): Promise<string> => {
        try {
            const { replicaId, universalId, fileName, fieldName, fieldIndex, force } = config;

            const request: RequestInit = {
                headers: {
                    universalId,
                    fileName: encodeURIComponent(fileName?.trim() || ''),
                    fieldName: fieldName,
                    fieldIndex: fieldIndex,
                    force: force ? 'true' : null,
                },
            };

            const url = urlRegisterDocumentFile.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that registers the document for the pdfviewer.
     * @param config - Object that contains the document information to know which file needs should be opened.
     * @returns Promise for the HTTP request.
     */
    checkDocumentFile: async (config: {
        replicaId: string;
        universalId: string;
        fieldName: string;
        fieldIndex: string;
        callbackURL: string;
        fileName?: string;
    }): Promise<{ response: string; callbackURL: string }> => {
        try {
            const { replicaId, universalId, fileName, fieldName, fieldIndex, callbackURL } = config;

            const request: RequestInit = {
                headers: {
                    universalId,
                    fileName: encodeURIComponent(fileName?.trim() || ''),
                    fieldName: fieldName,
                    fieldIndex: fieldIndex,
                },
            };

            const url = urlCheckDocumentFile.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve({ response: (await response.text()) || 'ok', callbackURL })
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<SliceProps>) => {
        /**
         * Reducers for the 'openDocumentFile' thunk.
         */
        builder.addCase(openDocumentFile.pending, (state: Draft<IProjectFileOpenState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            openDocumentFile.fulfilled,
            (state: Draft<IProjectFileOpenState>, action: IAction<string>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            openDocumentFile.rejected,
            (state: Draft<IProjectFileOpenState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'checkDocumentFile' thunk.
         */
        builder.addCase(checkDocumentFile.pending, (state: Draft<IProjectFileOpenState>) => {
            state.checkLoading = true;
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            checkDocumentFile.fulfilled,
            (state: Draft<IProjectFileOpenState>, action) => {
                state.checkLoading = false;
                state.loading = false;
                state.error = null;

                const { universalId, fieldName, fieldIndex } = action.meta.arg;
                const { response, callbackURL } = action.payload;

                const newCheckResult = {
                    file: `${universalId}-${fieldName}-${fieldIndex}`,
                    response,
                    callbackURL,
                };
                state.checkResults = [...state.checkResults, newCheckResult];
            },
        );
        builder.addCase(
            checkDocumentFile.rejected,
            (state: Draft<IProjectFileOpenState>, action: IAction<SerializedError>) => {
                state.checkLoading = false;
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};
/**
 * Expose thunk functions as exports.
 */
export const openDocumentFile = createAsyncThunk(
    'project/openDocumentFile',
    thunks.openDocumentFile,
);

export const checkDocumentFile = createAsyncThunk(
    'project/checkDocumentFile',
    thunks.checkDocumentFile,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectFileOpenState extends SlicePropsWithData<string> {
    checkLoading: boolean;
    checkResults: { file: string; response: string; callbackURL: string }[];
    webDAVDecisionDialog: IWebDavDecisionDialogConfig | null;
    fileSelectDialog: IFileSelectionDialogConfig | null;
    isViewFileErrorDialogOpen: boolean;
}

/**
 * Create the redux slice.
 */
export const projectFileOpenSlice = createSlice({
    name: 'projectFileOpen',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const {
    showFileSelectDialog,
    showViewFileErrorDialog,
    showWebDAVDecisionDialog,
    resetCheckResult,
    resetFileOpenError,
} = projectFileOpenSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectFileOpenSlice.reducer;
